import React, { useState } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WorkshopSelection from './components/Workshop';

const styles = {
    // General styles
    appContainer: {
        background: 'linear-gradient(135deg, #0a0a2e 0%, #16213e 30%, #0f3460 70%, #1a0b2e 100%)',
        color: 'white',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        overflowX: 'hidden',
    },
    // Navbar Placeholder
    navbar: {
        width: '100%',
        padding: '20px 40px',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
        boxSizing: 'border-box',
        background: 'rgba(10, 10, 46, 0.3)',
        backdropFilter: 'blur(10px)',
    },
    navLogo: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: 'white',
    },
    navLinks: {
        display: 'flex',
        gap: '30px',
    },
    navLink: {
        color: '#e5e7eb',
        textDecoration: 'none',
        fontWeight: 500,
        transition: 'color 0.3s ease',
    },
    // AIWorkshopLanding Section
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8em 20px',
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    backgroundOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: `
        radial-gradient(circle at 20% 80%, rgba(255, 20, 147, 0.3) 0%, transparent 40%),
        radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.3) 0%, transparent 40%)
      `,
      pointerEvents: 'none'
    },
    networkMesh: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundImage: `
        linear-gradient(rgba(255, 20, 147, 0.07) 1px, transparent 1px),
        linear-gradient(90deg, rgba(138, 43, 226, 0.07) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      pointerEvents: 'none',
      opacity: 0.5
    },
    flowingLines: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: `
        linear-gradient(45deg, transparent 40%, rgba(255, 20, 147, 0.1) 50%, transparent 60%)
      `,
      backgroundSize: '200px 200px',
      animation: 'flowLines 20s linear infinite',
      pointerEvents: 'none'
    },
    contentWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '1200px',
      width: '100%',
      textAlign: 'center',
      position: 'relative',
      zIndex: 2
    },
    badge: {
      backgroundColor: 'rgba(79, 124, 255, 0.15)',
      border: '1px solid rgba(79, 124, 255, 0.4)',
      borderRadius: '25px',
      padding: '10px 24px',
      fontSize: '0.9rem',
      fontWeight: 500,
      marginBottom: '32px',
      backdropFilter: 'blur(15px)',
      color: '#87ceeb',
      boxShadow: '0 4px 20px rgba(79, 124, 255, 0.2)'
    },
    mainTitle: {
      fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
      fontWeight: 800,
      lineHeight: 1.1,
      marginBottom: '24px',
      textAlign: 'center',
      maxWidth: '900px',
      textShadow: '0 0 30px rgba(255, 20, 147, 0.3)'
    },
    titlePart2: {
      background: 'linear-gradient(135deg, #ff1493 0%, #8a2be2 50%, #4f7cff 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      filter: 'drop-shadow(0 0 10px rgba(255, 20, 147, 0.3))'
    },
    description: {
      fontSize: 'clamp(1rem, 4vw, 1.25rem)',
      lineHeight: 1.6,
      color: 'rgba(255, 255, 255, 0.85)',
      marginBottom: '40px',
      maxWidth: '700px',
      textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
    },
    featuresContainer: {
      display: 'flex',
      gap: '16px',
      marginBottom: '48px',
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    featureChip: {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '25px',
      padding: '12px 24px',
      fontSize: '0.95rem',
      fontWeight: 500,
      color: 'white',
      backdropFilter: 'blur(15px)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease'
    },
    exploreBtn: {
      background: 'linear-gradient(135deg, #ff1493 0%, #8a2be2 100%)',
      color: 'white',
      border: 'none',
      padding: '18px 40px',
      borderRadius: '30px',
      fontSize: '1.1rem',
      fontWeight: 600,
      cursor: 'default',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 32px rgba(255, 20, 147, 0.4)',
      backdropFilter: 'blur(10px)',
      position: 'relative',
      overflow: 'hidden'
    },
    particle: {
      position: 'absolute',
      borderRadius: '50%',
      opacity: 0.7,
      animation: 'floatParticle 8s ease-in-out infinite'
    },
    // Placeholder Section Styles
    placeholderSection: {
        padding: '100px 20px',
        textAlign: 'center',
        background: '#0a0a2e'
    },
    placeholderTitle: {
        fontSize: 'clamp(2rem, 6vw, 3rem)',
        marginBottom: '20px',
        background: 'linear-gradient(135deg, #ff1493, #8a2be2)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    },
    // Footer Placeholder
    footer: {
        background: '#0a0a2e',
        padding: '40px 20px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    footerText: {
        color: '#9ca3af',
        fontSize: '14px',
    },
};

const AIWorkshopLanding1 = () => {
  const handleExploreHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
    e.currentTarget.style.boxShadow = '0 15px 50px rgba(255, 20, 147, 0.5)';
  };
  const handleExploreLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 20, 147, 0.4)';
  };
  const handleChipHover = (e) => {
    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
    e.currentTarget.style.transform = 'translateY(-2px)';
  };
  const handleChipLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
    e.currentTarget.style.transform = 'translateY(0)';
  };

  const createParticles = () => {
    const particles = [];
    const colors = ['#ff1493', '#8a2be2', '#4f7cff'];
    for (let i = 0; i < 12; i++) {
      const size = Math.random() * 5 + 2;
      particles.push(
        <div
          key={i}
          style={{
            ...styles.particle,
            width: `${size}px`, height: `${size}px`,
            top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`,
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${8 + Math.random() * 8}s`,
            boxShadow: `0 0 ${size * 2}px currentColor`
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundOverlay}></div>
      <div style={styles.networkMesh}></div>
      <div style={styles.flowingLines}></div>
      {createParticles()}

      <div style={styles.contentWrapper}>
        <div style={styles.badge}>🌐 Global AI Society</div>
        <h1 style={styles.mainTitle}>
          <span style={{color: 'white'}}>Master the Future of</span>
          <br />
          <span style={styles.titlePart2}>Artificial Intelligence</span>
        </h1>
        <p style={styles.description}>
          Join our intensive workshops and bootcamps designed to transform you into an AI expert. Learn from industry leaders and build real-world projects that matter.
        </p>
        <div style={styles.featuresContainer}>
          {['🚀 Hands-on Projects', '👨‍🏫 Expert Instructors', '🏢 Industry Relevant'].map(text => (
            <div 
              key={text}
              style={styles.featureChip}
              onMouseEnter={handleChipHover}
              onMouseLeave={handleChipLeave}
            >
              {text}
            </div>
          ))}
        </div>
        <button 
          style={styles.exploreBtn}
          onMouseEnter={handleExploreHover}
          onMouseLeave={handleExploreLeave}
        >
          Explore Workshops
        </button>
      </div>
    </div>
  );
};

//================================================================
// MAIN APP COMPONENT
//================================================================
const AIWorkshopLanding = () => {
  return (
    <div style={styles.appContainer}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;800&display=swap');
        
        html { scroll-behavior: smooth; }
        body { margin: 0; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }

        @keyframes floatParticle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.7; }
          50% { transform: translateY(-30px) translateX(20px); opacity: 1; }
        }
        
        @keyframes flowLines {
          from { background-position: 0 0; }
          to { background-position: 200px 200px; }
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
            .navbar-responsive {
                padding: 15px 20px;
            }
            .nav-links-responsive {
                display: none;
            }
        }
      `}</style>
      
      <Navbar />
      <main>
        <AIWorkshopLanding1 />
        <WorkshopSelection />
      </main>
      <Footer />
    </div>
  );
}

export default AIWorkshopLanding;